import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Container,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { openSuccessNotification } from "helpers/methods";
import { useState } from "react";
import homeStyles from "../Home.styles";
import { subscribeWebsite } from "../HomeServices";

const SubscribeSection = () => {
  const theme = useTheme();
  const classes = homeStyles;
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubscribe = async () => {
    try {
      if (!email || !email.includes("@")) {
        return;
      }
      setIsSubmitting(true);
      const response = await subscribeWebsite(email);
      openSuccessNotification(response?.message || "Subscription successful");
      console.log(response);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubscribe();
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes subscribeReveal {
            0% { opacity: 0; transform: translateY(30px) scale(0.95); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes subscribeSlideIn {
            0% { opacity: 0; transform: translateX(-30px); }
            100% { opacity: 1; transform: translateX(0); }
          }
          @keyframes subscribeFadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes backgroundFloat {
            0%, 100% { transform: translate(0, 0) rotate(0deg); }
            50% { transform: translate(20px, -15px) rotate(5deg); }
          }
          @keyframes backgroundPulse {
            0%, 100% { opacity: 0.4; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
          @keyframes particleFloat {
            0%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 0.3; }
            25% { transform: translate(15px, -20px) rotate(90deg); opacity: 0.6; }
            50% { transform: translate(-10px, -30px) rotate(180deg); opacity: 0.4; }
            75% { transform: translate(-20px, 10px) rotate(270deg); opacity: 0.5; }
          }
          @keyframes waveAnimation {
            0%, 100% { transform: translateX(0) translateY(0); }
            50% { transform: translateX(20px) translateY(-15px); }
          }
          @keyframes rotateSlow {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes morphShape {
            0%, 100% { border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%; }
            50% { border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%; }
          }
          @keyframes gradientShift {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
        `}
      </style>
      <Box
        component="section"
        sx={{
          position: "relative",
          width: "100%",
          py: { xs: 7, md: 10 },
          overflow: "hidden",
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Enhanced Creative Background Elements */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Animated gradient mesh background */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.5 }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at 20% 30%, ${alpha(
                  "#f8481c",
                  0.05
                )} 0%, transparent 50%),
                           radial-gradient(circle at 80% 70%, ${alpha(
                             "#f8481c",
                             0.04
                           )} 0%, transparent 50%),
                           radial-gradient(circle at 50% 50%, ${alpha(
                             "#f8481c",
                             0.03
                           )} 0%, transparent 50%),
                           radial-gradient(circle at 10% 80%, ${alpha(
                             "#f8481c",
                             0.04
                           )} 0%, transparent 50%)`,
                backgroundSize: "200% 200%",
                animation: "gradientShift 20s ease infinite",
              }}
            />
          </motion.div>

          {/* Multiple animated gradient orbs */}
          {[
            { top: "5%", right: "3%", size: 400, delay: 0, duration: 20 },
            { bottom: "10%", left: "5%", size: 350, delay: 0.5, duration: 18 },
            { top: "50%", left: "50%", size: 250, delay: 0.3, duration: 15 },
            { top: "20%", left: "15%", size: 300, delay: 0.7, duration: 22 },
            {
              bottom: "20%",
              right: "10%",
              size: 280,
              delay: 0.4,
              duration: 19,
            },
          ].map((orb, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              animate={{
                x: [0, 25 + i * 5, -15 - i * 3, 0],
                y: [0, -20 - i * 3, 15 + i * 2, 0],
                scale: [1, 1.1 + i * 0.05, 0.95 - i * 0.02, 1],
              }}
              transition={{
                opacity: { duration: 1.2, delay: orb.delay },
                scale: { duration: 1.2, delay: orb.delay },
                x: {
                  duration: orb.duration,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                y: {
                  duration: orb.duration,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  ...(orb.top && { top: orb.top }),
                  ...(orb.bottom && { bottom: orb.bottom }),
                  ...(orb.left && { left: orb.left }),
                  ...(orb.right && { right: orb.right }),
                  ...(orb.top === "50%" &&
                    orb.left === "50%" && {
                      transform: "translate(-50%, -50%)",
                    }),
                  width: `${orb.size}px`,
                  height: `${orb.size}px`,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    "#f8481c",
                    0.06 - i * 0.01
                  )} 0%, ${alpha(
                    "#f8481c",
                    0.03 - i * 0.005
                  )} 40%, transparent 70%)`,
                  filter: "blur(70px)",
                }}
              />
            </motion.div>
          ))}

          {/* Animated grid pattern with rotation */}
          <motion.div
            initial={{ opacity: 0, rotate: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.5,
              delay: 0.4,
              rotate: { duration: 100, repeat: Infinity, ease: "linear" },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `linear-gradient(${alpha(
                  theme.palette.divider,
                  0.03
                )} 1px, transparent 1px), 
                                 linear-gradient(90deg, ${alpha(
                                   theme.palette.divider,
                                   0.03
                                 )} 1px, transparent 1px)`,
                backgroundSize: "60px 60px",
                opacity: 0.6,
                transformOrigin: "center",
              }}
            />
          </motion.div>

          {/* Floating particles */}
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={`particle-${i}`}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              animate={{
                x: [0, Math.random() * 60 - 30, Math.random() * 40 - 20, 0],
                y: [0, Math.random() * 60 - 30, Math.random() * 40 - 20, 0],
                rotate: [0, 360],
                opacity: [0.2, 0.5, 0.3, 0.2],
              }}
              transition={{
                opacity: { duration: 0.8, delay: 0.5 + i * 0.1 },
                scale: { duration: 0.8, delay: 0.5 + i * 0.1 },
                x: {
                  duration: 12 + i * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                y: {
                  duration: 12 + i * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                rotate: {
                  duration: 20 + i * 3,
                  repeat: Infinity,
                  ease: "linear",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: `${5 + ((i * 7) % 90)}%`,
                  left: `${5 + ((i * 8) % 90)}%`,
                  width: `${4 + (i % 3)}px`,
                  height: `${4 + (i % 3)}px`,
                  borderRadius: "50%",
                  background: alpha("#f8481c", 0.3 + (i % 3) * 0.1),
                  boxShadow: `0 0 ${6 + i * 2}px ${alpha("#f8481c", 0.2)}`,
                }}
              />
            </motion.div>
          ))}

          {/* Morphing geometric shapes */}
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={`shape-${i}`}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              animate={{
                y: [0, -25 + i * 5, 0],
                rotate: [0, 15 + i * 5, 0],
                opacity: [0.15, 0.35, 0.15],
                scale: [1, 1.1, 1],
              }}
              transition={{
                opacity: { duration: 0.8, delay: 0.8 + i * 0.15 },
                scale: { duration: 0.8, delay: 0.8 + i * 0.15 },
                y: { duration: 8 + i * 2, repeat: Infinity, ease: "easeInOut" },
                rotate: {
                  duration: 10 + i * 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: `${15 + i * 18}%`,
                  left: `${8 + i * 18}%`,
                  width: `${50 + i * 15}px`,
                  height: `${50 + i * 15}px`,
                  borderRadius:
                    i === 0
                      ? "50%"
                      : i === 1
                      ? "20% 80% 20% 80% / 30% 70% 30% 70%"
                      : i === 2
                      ? "30% 70% 70% 30% / 30% 30% 70% 70%"
                      : i === 3
                      ? "60% 40% 40% 60% / 60% 30% 70% 40%"
                      : "40%",
                  background: `linear-gradient(${135 + i * 30}deg, ${alpha(
                    "#f8481c",
                    0.06 - i * 0.01
                  )} 0%, ${alpha("#f8481c", 0.02)} 50%, transparent 100%)`,
                  filter: "blur(3px)",
                  transform: `rotate(${i * 36}deg)`,
                  animation:
                    i === 1 || i === 3
                      ? "morphShape 8s ease-in-out infinite"
                      : "none",
                }}
              />
            </motion.div>
          ))}

          {/* Animated connecting lines */}
          {[...Array(4)].map((_, i) => (
            <motion.div
              key={`line-${i}`}
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              // transition={{ duration: 1, delay: 1 + i * 0.2 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scaleX: [1, 1.1, 1],
              }}
              transition={{
                opacity: {
                  duration: 3 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                scaleX: {
                  duration: 4 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: `${20 + i * 20}%`,
                  left: `${10 + i * 25}%`,
                  width: `${100 + i * 30}px`,
                  height: "2px",
                  background: `linear-gradient(90deg, transparent 0%, ${alpha(
                    "#f8481c",
                    0.2
                  )} 50%, transparent 100%)`,
                  transform: `rotate(${i * 15}deg)`,
                  transformOrigin: "left center",
                }}
              />
            </motion.div>
          ))}

          {/* Wave patterns */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`wave-${i}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              animate={{
                x: [0, 30, -20, 0],
                y: [0, -15, 10, 0],
              }}
              transition={{
                opacity: { duration: 1, delay: 1.2 + i * 0.2 },
                x: {
                  duration: 15 + i * 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                y: {
                  duration: 12 + i * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: `${30 + i * 25}%`,
                  left: `${5 + i * 30}%`,
                  width: `${200 + i * 50}px`,
                  height: `${80 + i * 20}px`,
                  background: `radial-gradient(ellipse, ${alpha(
                    "#f8481c",
                    0.03
                  )} 0%, transparent 70%)`,
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  transform: `rotate(${i * 20}deg)`,
                }}
              />
            </motion.div>
          ))}
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Stack spacing={5} alignItems="center">
            {/* Header Section */}
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: "100%", maxWidth: "700px" }}
            >
              <Stack spacing={2.5} alignItems="center" textAlign="center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "32px", md: "48px" },
                      fontWeight: 700,
                      background:
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 75%, #f8481c 100%)"
                          : "linear-gradient(135deg, #000000 0%, #000000 75%, #f8481c 100%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      letterSpacing: "-0.03em",
                      lineHeight: 1.1,
                    }}
                  >
                    Never Miss an Update
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "16px", md: "18px" },
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      maxWidth: "600px",
                      px: { xs: 2, md: 0 },
                      fontWeight: 400,
                    }}
                  >
                    Subscribe to our newsletter and get the latest articles,
                    insights, and updates delivered directly to your inbox.
                  </Typography>
                </motion.div>
              </Stack>
            </motion.div>

            {/* Subscribe Card */}
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{
                duration: 0.8,
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1],
              }}
              style={{ width: "100%", maxWidth: "650px" }}
            >
              <Box
                sx={{
                  position: "relative",
                  background:
                    theme.palette.mode === "dark"
                      ? `linear-gradient(135deg, ${alpha(
                          "#1b1b1b",
                          0.95
                        )} 0%, ${alpha("#252525", 0.95)} 100%)`
                      : `linear-gradient(135deg, ${alpha(
                          "#ffffff",
                          0.95
                        )} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
                  borderRadius: "12px",
                  p: { xs: 3, md: 4 },
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  boxShadow: `0 4px 24px ${alpha(
                    theme.palette.mode === "dark" ? "#000000" : "#000000",
                    0.1
                  )}`,
                  backdropFilter: "blur(20px)",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: `linear-gradient(90deg, transparent 0%, ${alpha(
                      "#f8481c",
                      0.6
                    )} 50%, transparent 100%)`,
                  },
                }}
              >
                <Stack spacing={3}>
                  {/* Form */}
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    <TextField
                      fullWidth
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting || isSubmitted}
                      sx={{
                        flex: 1,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? alpha("#1b1b1b", 0.6)
                              : alpha("#ffffff", 0.8),
                          borderRadius: "8px",
                          border: `1px solid ${alpha(
                            theme.palette.divider,
                            0.2
                          )}`,
                          transition: "all 0.3s ease",
                          pr: { xs: 1, sm: "72px" },
                          "&:hover": {
                            border: `1px solid ${alpha("#f8481c", 0.3)}`,
                          },
                          "&.Mui-focused": {
                            border: `1px solid ${alpha("#f8481c", 0.5)}`,
                            boxShadow: `0 0 0 3px ${alpha("#f8481c", 0.1)}`,
                          },
                          "& fieldset": {
                            border: "none",
                          },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: { xs: "15px", md: "16px" },
                          py: { xs: 1.5, sm: 1.75 },
                          color: theme.palette.text.primary,
                          "&::placeholder": {
                            color: theme.palette.text.secondary,
                            opacity: 0.6,
                          },
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <EmailIcon
                            sx={{
                              color: alpha("#f8481c", 0.7),
                              mr: 1.5,
                              fontSize: "22px",
                            }}
                          />
                        ),
                      }}
                    />

                    {/* Creative Compact Button */}
                    <Box
                      sx={{
                        position: { xs: "relative", sm: "absolute" },
                        right: { xs: "auto", sm: "8px" },
                        top: { xs: "auto", sm: "50%" },
                        transform: { xs: "none", sm: "translateY(-50%)" },
                        zIndex: 1,
                      }}
                    >
                      <motion.div
                        initial={{ opacity: 0, x: 20, scale: 0.9 }}
                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.5 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          onClick={handleSubscribe}
                          disabled={
                            !email ||
                            !email.includes("@") ||
                            isSubmitting ||
                            isSubmitted
                          }
                          sx={{
                            minWidth: { xs: "100%", sm: "56px" },
                            width: { xs: "100%", sm: "56px" },
                            height: { xs: "48px", sm: "48px" },
                            borderRadius: "8px",
                            background: isSubmitted
                              ? `linear-gradient(135deg, #4caf50 0%, #45a049 100%)`
                              : `linear-gradient(135deg, #f8481c 0%, #e04119 100%)`,
                            color: "#ffffff",
                            fontSize: { xs: "15px", md: "16px" },
                            fontWeight: 600,
                            textTransform: "none",
                            boxShadow: isSubmitted
                              ? `0 4px 16px ${alpha("#4caf50", 0.3)}`
                              : `0 4px 16px ${alpha("#f8481c", 0.3)}`,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: "-100%",
                              width: "100%",
                              height: "100%",
                              background:
                                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                              transition: "left 0.5s ease",
                            },
                            "&:hover:not(:disabled)": {
                              background: isSubmitted
                                ? `linear-gradient(135deg, #4caf50 0%, #45a049 100%)`
                                : `linear-gradient(135deg, #e04119 0%, #d03a16 100%)`,
                              boxShadow: isSubmitted
                                ? `0 6px 24px ${alpha("#4caf50", 0.4)}`
                                : `0 6px 24px ${alpha("#f8481c", 0.4)}`,
                              transform: "translateY(-2px)",
                              "&::before": {
                                left: "100%",
                              },
                            },
                            "&:disabled": {
                              opacity: 0.6,
                            },
                            px: { xs: 2, sm: 0 },
                          }}
                        >
                          {isSubmitting ? (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                duration: 1,
                                repeat: Infinity,
                                ease: "linear",
                              }}
                            >
                              <SendIcon
                                sx={{ fontSize: { xs: "20px", sm: "24px" } }}
                              />
                            </motion.div>
                          ) : isSubmitted ? (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <CheckCircleIcon
                                sx={{ fontSize: { xs: "20px", sm: "24px" } }}
                              />
                            </motion.div>
                          ) : (
                            <>
                              <SendIcon
                                sx={{
                                  fontSize: { xs: "20px", sm: "24px" },
                                  display: { xs: "none", sm: "block" },
                                }}
                              />
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{ display: { xs: "flex", sm: "none" } }}
                              >
                                <SendIcon sx={{ fontSize: "20px" }} />
                                <Typography sx={{ fontSize: "inherit" }}>
                                  Subscribe
                                </Typography>
                              </Stack>
                            </>
                          )}
                        </Button>
                      </motion.div>
                    </Box>
                  </Box>

                  {/* Features */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 2, sm: 3 }}
                    justifyContent="center"
                    sx={{
                      pt: 2,
                      borderTop: `1px solid ${alpha(
                        theme.palette.divider,
                        0.1
                      )}`,
                    }}
                  >
                    {[
                      { icon: "✓", text: "Daily updates" },
                      { icon: "✓", text: "No spam" },
                      { icon: "✓", text: "Unsubscribe anytime" },
                    ].map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            px: { xs: 0, sm: 1 },
                          }}
                        >
                          <Box
                            sx={{
                              width: "20px",
                              height: "20px",
                              borderRadius: "50%",
                              background: alpha("#f8481c", 0.1),
                              border: `1.5px solid ${alpha("#f8481c", 0.3)}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#f8481c",
                                fontWeight: 600,
                                lineHeight: 1,
                              }}
                            >
                              {feature.icon}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: { xs: "13px", md: "14px" },
                              color: theme.palette.text.secondary,
                              fontWeight: 400,
                            }}
                          >
                            {feature.text}
                          </Typography>
                        </Stack>
                      </motion.div>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </motion.div>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default SubscribeSection;
